import clsx from 'clsx';
import { useState } from 'react';
import { useSwiperSlide } from 'swiper/react';
import { Video } from '~/shared/components/Video';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { breakpointsRaw } from '~/theme/breakpoints';
import styles from './SwiperVideo.module.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    src: string;
    /**
     * Whether arriving at this slide is allowed to start the video. False when the slider was
     * moved by a thumbnail taking focus, since merely tabbing past a video should not play it.
     */
    playOnActive?: boolean;
}

/**
 * A video that knows which slide it is on. Only usable inside a `SwiperSlide`, since
 * `useSwiperSlide` reads a context that slide provides - `isActive` is React state fed by
 * Swiper's own `_slideClass` event, so it moves with the slider rather than being read off a
 * mutable field the way `swiper.activeIndex` would be.
 */
export const SwiperVideo: React.FC<Props> = ({ src, playOnActive = true, className, ...rest }) => {
    const { isActive } = useSwiperSlide();
    const [hasBeenPlayed, setHasBeenPlayed] = useState(false);

    const isTouchGallery = useIsMobile({ width: breakpointsRaw.sm });

    return (
        <div className={clsx(styles.swiperVideo, className)} {...rest}>
            <Video
                src={src}
                /*
                 * Arriving at the slide starts the video on desktop; on touch the person asks for
                 * it themselves, and `hasBeenPlayed` then carries that decision across leaving and
                 * returning to the slide.
                 */
                playing={isActive && playOnActive && (!isTouchGallery || hasBeenPlayed)}
                /*
                 * Muted only where we start it unasked, which is the only way a browser permits an
                 * unprompted start at all. The value is stable across slide changes on purpose:
                 * react-player only acts on a change, so someone who unmutes stays unmuted.
                 */
                muted={!isTouchGallery}
                // After the video has been played, we will make use of the native players' controls.
                showPlayButton={!hasBeenPlayed}
                onPlay={() => setHasBeenPlayed(true)}
            />
        </div>
    );
};
